import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Timer, Search, RotateCcw, Layers, Shapes, Video } from "lucide-react";
import { api } from "@/lib/api";
import {
  EXAM_LABELS,
  SUBJECT_LABELS,
  DIFFICULTY_LABELS,
  difficultyClass,
  subjectAccent,
  examsForPref,
  subjectsForPref,
} from "@/lib/pyqMeta";
import { useAuth } from "@/context/AuthContext";
import { MathText } from "@/components/MathText";
import { BookmarkButton } from "@/components/BookmarkButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={cn(
      "rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
      active
        ? "border-primary bg-primary text-primary-foreground"
        : "border-border bg-secondary text-muted-foreground hover:text-foreground"
    )}
  >
    {children}
  </button>
);

const Section = ({ label, children }) => (
  <div className="space-y-2.5">
    <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export default function PYQBrowser() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const pref = user?.examPreference || "JEE";
  const allowedExams = examsForPref(pref);
  const allowedSubjects = subjectsForPref(pref);
  const [meta, setMeta] = useState(null);
  const [exam, setExam] = useState("");
  const [subject, setSubject] = useState("");
  const [chapter, setChapter] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState("UNTIMED");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [bookmarks, setBookmarks] = useState(new Set());

  useEffect(() => {
    api.get("/pyq/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/bookmarks/ids").then((r) => setBookmarks(new Set(r.data))).catch(() => {});
  }, []);

  const toggleBookmark = (id, on) =>
    setBookmarks((prev) => {
      const n = new Set(prev);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });

  const subjects = useMemo(() => {
    if (!meta) return [];
    let base;
    if (exam && meta.examSubjects[exam]) base = meta.examSubjects[exam];
    else {
      const all = new Set();
      allowedExams.forEach((e) => (meta.examSubjects[e] || []).forEach((s) => all.add(s)));
      base = [...all];
    }
    return base.filter((s) => allowedSubjects.includes(s));
  }, [meta, exam, allowedExams, allowedSubjects]);

  const chapters = useMemo(() => {
    if (!meta || !subject) return [];
    return meta.chaptersBySubject[subject] || [];
  }, [meta, subject]);

  const fetchList = () => {
    setLoading(true);
    const params = {};
    if (exam) params.exam = exam;
    if (subject) params.subject = subject;
    if (chapter) params.chapter = chapter;
    if (difficulty) params.difficulty = difficulty;
    if (search.trim()) params.search = search.trim();
    api
      .get("/pyq", { params })
      .then((r) => {
        setItems(r.data.items);
        setTotal(r.data.total);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(fetchList, search ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exam, subject, chapter, difficulty, search]);

  const resetFilters = () => {
    setExam("");
    setSubject("");
    setChapter("");
    setDifficulty("");
    setSearch("");
  };

  const startSession = () => {
    const params = new URLSearchParams();
    if (exam) params.set("exam", exam);
    if (subject) params.set("subject", subject);
    if (chapter) params.set("chapter", chapter);
    if (difficulty) params.set("difficulty", difficulty);
    if (search.trim()) params.set("search", search.trim());
    params.set("mode", mode);
    navigate(`/student/session?${params.toString()}`);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">
            Previous Year Questions
          </h1>
          <p className="mt-2 text-muted-foreground">
            Filter authentic past-paper questions and launch a practice session.
          </p>
        </div>
        <Button asChild variant="outline" data-testid="go-chapters-btn" className="rounded-full">
          <Link to="/student/weak-areas"><Layers className="mr-1.5 h-4 w-4" /> Chapter Practice</Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        {/* Filters */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="sticky top-20 space-y-6 rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <span className="font-heading text-lg font-semibold">Filters</span>
              <Button
                variant="ghost"
                size="sm"
                data-testid="reset-filters-btn"
                onClick={resetFilters}
                className="h-8 gap-1.5 text-xs text-muted-foreground"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Reset
              </Button>
            </div>

            <Section label="Exam">
              <Chip active={!exam} onClick={() => { setExam(""); }} testid="filter-exam-all">All</Chip>
              {meta &&
                Object.keys(meta.examSubjects)
                  .filter((e) => allowedExams.includes(e))
                  .map((e) => (
                  <Chip
                    key={e}
                    active={exam === e}
                    onClick={() => {
                      setExam(exam === e ? "" : e);
                      setSubject("");
                      setChapter("");
                    }}
                    testid={`filter-exam-${e}`}
                  >
                    {EXAM_LABELS[e] || e}
                  </Chip>
                ))}
            </Section>

            <Section label="Difficulty">
              <Chip active={!difficulty} onClick={() => setDifficulty("")} testid="filter-diff-all">All</Chip>
              {["EASY", "MEDIUM", "HARD"].map((d) => (
                <Chip
                  key={d}
                  active={difficulty === d}
                  onClick={() => setDifficulty(difficulty === d ? "" : d)}
                  testid={`filter-diff-${d}`}
                >
                  {DIFFICULTY_LABELS[d]}
                </Chip>
              ))}
            </Section>

            <div className="space-y-2.5">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Subject</p>
              <Select value={subject || "all"} onValueChange={(v) => { setSubject(v === "all" ? "" : v); setChapter(""); }}>
                <SelectTrigger data-testid="filter-subject" className="rounded-xl">
                  <SelectValue placeholder="All subjects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All subjects</SelectItem>
                  {subjects.map((s) => (
                    <SelectItem key={s} value={s}>{SUBJECT_LABELS[s] || s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2.5">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Chapter</p>
              <Select value={chapter || "all"} onValueChange={(v) => setChapter(v === "all" ? "" : v)} disabled={!subject}>
                <SelectTrigger data-testid="filter-chapter" className="rounded-xl">
                  <SelectValue placeholder={subject ? "All chapters" : "Pick a subject first"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All chapters</SelectItem>
                  {chapters.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="filter-search"
                placeholder="Search questions…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="rounded-xl pl-9"
              />
            </div>
          </div>
        </aside>

        {/* Feed */}
        <div className="lg:col-span-8 xl:col-span-9">
          {/* Launch bar */}
          <div className="mb-6 flex flex-col gap-4 rounded-2xl border border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary text-primary">
                <Layers className="h-5 w-5" />
              </span>
              <div>
                <div className="font-heading text-lg font-semibold" data-testid="result-count">
                  {loading ? "…" : total} question{total === 1 ? "" : "s"}
                </div>
                <div className="text-xs text-muted-foreground">matching your filters</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex rounded-full border border-border p-1">
                {["UNTIMED", "TIMED"].map((m) => (
                  <button
                    key={m}
                    data-testid={`mode-${m}`}
                    onClick={() => setMode(m)}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                      mode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                    )}
                  >
                    {m === "TIMED" && <Timer className="h-3.5 w-3.5" />}
                    {m === "TIMED" ? "Timed" : "Untimed"}
                  </button>
                ))}
              </div>
              <Button
                data-testid="start-session-btn"
                onClick={startSession}
                disabled={loading || total === 0}
                className="rounded-full px-6"
              >
                <Play className="mr-1.5 h-4 w-4" /> Start
              </Button>
            </div>
          </div>

          {/* List */}
          <div className="space-y-4">
            {loading &&
              [1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-28 w-full rounded-2xl" />)}

            {!loading && items.length === 0 && (
              <div className="rounded-2xl border border-dashed border-border bg-card p-14 text-center">
                <p className="font-heading text-lg font-semibold">No questions found</p>
                <p className="mt-1 text-sm text-muted-foreground">Try loosening your filters.</p>
              </div>
            )}

            {!loading &&
              items.map((q, idx) => (
                <motion.div
                  key={q.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: Math.min(idx * 0.03, 0.3) }}
                  data-testid={`pyq-card-${q.id}`}
                  className="group rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary/40"
                >
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="rounded-full border-border font-medium">
                      {EXAM_LABELS[q.examType] || q.examType}
                    </Badge>
                    <span className={cn("text-xs font-semibold uppercase tracking-wide", subjectAccent(q.subject))}>
                      {SUBJECT_LABELS[q.subject] || q.subject}
                    </span>
                    <Badge className={cn("rounded-full border", difficultyClass(q.difficulty))}>
                      {DIFFICULTY_LABELS[q.difficulty]}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {q.year}{q.month ? ` · ${q.month}` : ""}{q.shift ? ` · ${q.shift}` : ""}
                    </span>
                    {q.diagram && (
                      <Badge variant="secondary" className="rounded-full text-[10px]">
                        <Shapes className="mr-1 h-3 w-3" /> Figure
                      </Badge>
                    )}
                    {q.videoEligible && (
                      <Badge
                        data-testid={`video-badge-${q.id}`}
                        className="rounded-full border-primary/30 bg-primary/10 text-[10px] text-primary hover:bg-primary/15"
                      >
                        <Video className="mr-1 h-3 w-3" /> Video solution
                      </Badge>
                    )}
                    <BookmarkButton
                      questionId={q.id}
                      active={bookmarks.has(q.id)}
                      onChange={toggleBookmark}
                      size="sm"
                      className="ml-auto"
                    />
                  </div>
                  <MathText
                    block
                    className="line-clamp-3 text-[15px] leading-relaxed text-foreground/90"
                    text={q.questionText}
                  />
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{q.chapter}</span>
                    {q.questionType === "MCQ_MULTIPLE" && (
                      <Badge variant="secondary" className="rounded-full text-[10px]">Multiple correct</Badge>
                    )}
                  </div>
                </motion.div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
